/** "Combine into one tour…": several selected products become one tour (plan
 *  2026-10-11 product-sync joins §7, D4, D5).
 *
 *  One product CREATES the tour, keyed by its own ID (the primary); the others
 *  are not built and their IDs become ops-owned additional IDs of that tour
 *  (the members). The dialog edits a draft of the run's join state and hands
 *  the result back on confirm; nothing is written until the run starts.
 *
 *  Every draft change is checked by `POST /ops/sync_products/joins/preview/`
 *  (debounced), which runs the same planner as the start: the dialog renders
 *  the server's owners, booking counts and problems. Until that answer is in,
 *  the listing-only mirror (`joinProblems`) stands in; once it is, the server's
 *  problems win.
 *
 *  A member whose ID another tour already holds (its own ID, or an additional
 *  ID) gets a required choice with no default (D4): leave it out, or move the
 *  ID to this tour. A move spells out what happens to the old tour and its
 *  bookings, and can also move the member's future bookings (D5), which asks
 *  the Edit-booking task's two run-wide questions, again with no default.
 *
 *  A would-be primary whose key is an additional ID of another tour cannot
 *  create the tour as it stands (J8); with the integration's
 *  `product_split_off` feature its row offers the same "Split off and build
 *  its own tour…" as the listing row (D2). The tour it is split off is the
 *  preview's (`target.aliased_tour_id`), never guessed from the listing.
 */
import CancelIcon from '@mui/icons-material/Cancel';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Radio,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Fragment, useEffect, useMemo, useState } from 'react';

import { isCatalogueNotLoaded } from '@/api/client';
import * as syncApi from '@/api/sync';
import type {
  BookingMoveChoice,
  ProductJoinProblem,
  ProductJoinsPreview,
  ProductJoinsPreviewInput,
  ProductJoinsPreviewMember,
  ProductListItem,
} from '@/api/sync';

import { useDebouncedValue } from './catalogue/NameControl';
import {
  answered,
  BookingMoveAnswers,
  consentTours,
  groupHolders,
  holdersOfMember,
  listLabels,
  OwnershipBlock,
  tourLabel,
  type HoldingTour,
  type OwnershipChoice,
} from './JoinOwnership';
import { SplitOffDialog } from './SplitOffDialog';
import { mintForProduct } from './mappingPolicy';
import {
  combine,
  defaultPrimary,
  joinProblems,
  ownership,
  setBookingMove,
  setMoveBookings,
  setMoveConsent,
  setSplitOff,
  targetTourId,
  toStartPayload,
  type JoinContext,
  type JoinState,
} from './productJoins';

/** How long the draft must stand still before the preview is asked. */
export const JOIN_PREVIEW_DEBOUNCE_MS = 300;

// ---------------------------------------------------------------------------
// Who holds a member's IDs, from the server's preview or the listing
// ---------------------------------------------------------------------------

/** The tours other than the combined one that hold a member's IDs. From the
 *  server's member entry when there is one (it knows parked names, take-overs
 *  and deleted holders), else from the listing's mappings. */
export function holdingTours(
  server: ProductJoinsPreviewMember | undefined,
  product: ProductListItem,
  ctx: Pick<JoinContext, 'mappings' | 'policy'>,
  targetId: number | null,
): HoldingTour[] {
  if (server) return holdersOfMember(server);
  const mappings = product.id ? ctx.mappings[product.id] : undefined;
  return groupHolders(
    ownership(product, mappings, ctx.policy).flatMap((o) =>
      !o.tour || o.tour.id === targetId
        ? []
        : [
            {
              id: o.tour.id,
              name: o.tour.name,
              value: {
                value: o.value,
                kind: o.kind === 'none' ? null : o.kind,
                parkedAs: null,
                managed: false,
                compoundSiblings: [],
              },
            },
          ],
    ),
  );
}

/** Problems a prompt on screen already asks about (the ownership choice, the
 *  booking-move answers): listing them too would say the same thing twice. */
const OWNERSHIP_PROMPT = new Set(['owned_elsewhere']);
const ANSWERS_PROMPT = new Set(['booking_move_answers_missing', 'booking_move_unanswered']);

// ---------------------------------------------------------------------------
// The dialog
// ---------------------------------------------------------------------------

export interface CombineProductsDialogProps {
  onClose: () => void;
  /** The run's join state with this dialog's group added. */
  onConfirm: (next: JoinState) => void;
  scope: { tour_operator_id: number; integration: string; account: string | null };
  /** The run's join state: other groups and split-offs, kept as they are. */
  state: JoinState;
  /** The listing, the run's policy and its whole selection. */
  ctx: JoinContext;
  /** The products to combine: selected and not yet joined, in listing order. */
  productIds: string[];
  /** The integration has `product_split_off`. */
  canSplitOff: boolean;
}

export function CombineProductsDialog({
  onClose,
  onConfirm,
  scope,
  state,
  ctx,
  productIds,
  canSplitOff,
}: CombineProductsDialogProps) {
  const byId = useMemo(
    () => new Map(ctx.products.flatMap((p) => (p.id ? [[p.id, p] as const] : []))),
    [ctx.products],
  );
  const nameOf = (id: string) => byId.get(id)?.name || id;

  const [included, setIncluded] = useState<Set<string>>(() => new Set(productIds));
  const [primary, setPrimary] = useState<string>(
    () => defaultPrimary(productIds, ctx) ?? productIds[0] ?? '',
  );
  const [choices, setChoices] = useState<Map<string, OwnershipChoice>>(new Map());
  const [moveBookings, setMoveBookingsDraft] = useState<Set<string>>(new Set());
  // Answers already given for the run (another group moves bookings) carry over.
  const [answers, setAnswers] = useState<Partial<BookingMoveChoice>>(
    () => state.bookingMove ?? {},
  );
  // The last server entry seen per member: a member that is left out is not
  // in the preview any more, but its warning stays on screen.
  const [seen, setSeen] = useState<Map<string, ProductJoinsPreviewMember>>(new Map());
  // Split-offs of these products, starting from the run's (D2).
  const [splitOffs, setSplitOffs] = useState<Map<string, number>>(
    () => new Map([...state.splitOff].filter(([id]) => productIds.includes(id))),
  );
  const [splitAsk, setSplitAsk] = useState<{ id: string; tour: { id: number; name: string | null } } | null>(
    null,
  );

  const preview = useJoinsPreview(scope, ctx, state, {
    productIds,
    included,
    primary,
    choices,
    moveBookings,
    answers,
    splitOffs,
  });
  const data = preview.data;

  const idsKey = productIds.join('\u0000');
  useEffect(() => {
    if (!data) return;
    const ids = new Set(idsKey.split('\u0000'));
    setSeen((prev) => {
      const next = new Map(prev);
      for (const g of data.groups) {
        for (const m of g.members) if (ids.has(m.product_id)) next.set(m.product_id, m);
      }
      return next;
    });
  }, [data, idsKey]);

  const group = data?.groups.find((g) => g.into_product_id === primary);
  const serverMember = (id: string) =>
    group?.members.find((m) => m.product_id === id) ?? seen.get(id);
  const clientTarget = targetTourId(primary, ctx);
  const targetId = group ? group.target.tour_id : clientTarget;

  // Per candidate member: who holds its IDs, and whether the operator answered.
  const rows = productIds.map((id) => {
    const product = byId.get(id)!;
    const isMember = included.has(id) && id !== primary;
    const holders = isMember ? holdingTours(serverMember(id), product, ctx, targetId) : [];
    const choice = choices.get(id);
    const needsChoice = holders.length > 0;
    // A "leave" is only ever given while the warning is on screen, and is
    // dropped when the primary changes, so it always has its warning.
    const leftOut = isMember && choice?.kind === 'leave';
    return {
      id,
      product,
      isMember,
      holders,
      choice: needsChoice && answered(choice, holders) ? choice : undefined,
      needsChoice,
      leftOut,
    };
  });
  const members = rows.filter((r) => r.isMember && !r.leftOut);
  const unanswered = rows.filter((r) => r.isMember && r.needsChoice && !r.choice);
  const movingBookings = members.filter(
    (r) => r.choice?.kind === 'move' && moveBookings.has(r.id),
  );
  const answersComplete = Boolean(answers.update_events && answers.old_tour_tags);

  const draft = buildDraftState(state, {
    candidates: productIds,
    splitOffs,
    primary,
    members: members.map((r) => r.id),
    consents: new Map(
      members.flatMap((r) => (r.choice?.kind === 'move' ? [[r.id, r.choice.tours] as const] : [])),
    ),
    moveBookings: new Set(movingBookings.map((r) => r.id)),
    answers,
  });

  // Problems: the server's once its answer matches the draft, else the mirror.
  const fresh = preview.isCurrent && data !== undefined;
  const unansweredIds = new Set(unanswered.map((r) => r.id));
  const radiosShown = movingBookings.length > 0;
  const prompted = (id: string | null, code: string) =>
    (OWNERSHIP_PROMPT.has(code) && id !== null && unansweredIds.has(id)) ||
    (ANSWERS_PROMPT.has(code) && radiosShown);
  const clientProblems = fresh ? null : joinProblems(draft, ctx);
  const problemsFor = (id: string): { detail: string }[] =>
    fresh
      ? data.problems.filter((p) => p.product_id === id && !prompted(id, p.code))
      : (clientProblems?.get(id) ?? []).filter((p) => !prompted(id, p.code));
  const runProblems: ProductJoinProblem[] = fresh
    ? data.problems.filter((p) => p.product_id === null && !prompted(null, p.code))
    : [];
  const blocking =
    runProblems.length > 0 ||
    rows.some((r) => (r.id === primary || (r.isMember && !r.leftOut)) && problemsFor(r.id).length);
  const catalogueGone = preview.isError && isCatalogueNotLoaded(preview.error);
  const canConfirm =
    members.length > 0 &&
    unanswered.length === 0 &&
    (!radiosShown || answersComplete) &&
    !blocking &&
    !catalogueGone &&
    // Wait for the server's verdict on exactly this draft, unless it could not
    // answer (the start checks it again).
    (fresh || (preview.isError && preview.settled));

  const primaryProduct = byId.get(primary);
  const key =
    group?.target.key ??
    (primaryProduct && ctx.policy?.mintable ? mintForProduct(ctx.policy, primaryProduct)[0]?.value : undefined);
  const memberValues = members.flatMap((r) =>
    ctx.policy?.mintable ? mintForProduct(ctx.policy, r.product).map((m) => m.value) : [],
  );
  // The tour the primary's key is an additional ID of (J8), as the server
  // sees it: a split-off takes the key from that tour. Set only for a key
  // that is exactly one ID.
  const aliasedTour =
    group?.target.aliased_tour_id != null
      ? { id: group.target.aliased_tour_id, name: group.target.aliased_tour_name }
      : null;
  // The split-off asked for here is the draft's; its name is the server's.
  const splitFromId = splitOffs.get(primary);
  const splitFrom =
    splitFromId === undefined
      ? undefined
      : { id: splitFromId, name: aliasedTour?.id === splitFromId ? aliasedTour.name : null };
  const splitTour = canSplitOff && splitFrom === undefined ? aliasedTour : null;
  const oldTours = [
    ...new Map(
      movingBookings.flatMap((r) => r.holders).flatMap((t) => (t.id === null ? [] : [[t.id, t] as const])),
    ).values(),
  ];

  const setChoice = (id: string, choice: OwnershipChoice | null) =>
    setChoices((prev) => {
      const next = new Map(prev);
      if (choice) next.set(id, choice);
      else next.delete(id);
      return next;
    });

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Combine into one tour</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            One product creates the tour, keyed by its own ID. The others are not built: their IDs
            become additional IDs of that tour, so their bookings land on it.
          </Typography>

          {primaryProduct && members.length > 0 && (
            <Typography variant="body2">
              <strong>{nameOf(primary)}</strong>{' '}
              {splitFrom
                ? `is split off ${tourLabel(splitFrom)} and creates the tour`
                : group?.target.tour_id
                  ? `updates its tour ${tourLabel({ id: group.target.tour_id, name: group.target.tour_name })}`
                  : 'creates the tour'}
              {key && (
                <>
                  , keyed <code>{key}</code>
                </>
              )}
              .{' '}
              {listLabels(members.map((r) => nameOf(r.id)))}{' '}
              {members.length === 1 ? 'joins' : 'join'} it as additional{' '}
              {memberValues.length === 1 ? 'ID' : 'IDs'}{' '}
              {memberValues.map((v, i) => (
                <Fragment key={v}>
                  {i > 0 && ', '}
                  <code>{v}</code>
                </Fragment>
              ))}
              .
            </Typography>
          )}
          {members.length === 0 && (
            <Alert severity="info">
              Nothing to combine: include at least one product besides the one that creates the
              tour.
            </Alert>
          )}

          {members.length > 0 && !fresh && !preview.isError ? (
            <LinearProgress aria-label="Checking the combination" />
          ) : (
            <Box sx={{ height: 4 }} />
          )}
          {catalogueGone && (
            <Alert severity="warning">
              The fetched catalogue is no longer cached, so the combination cannot be checked. Close
              this dialog and fetch the products again.
            </Alert>
          )}
          {preview.isError && !catalogueGone && (
            <Alert severity="warning">
              The combination could not be checked ({(preview.error as Error).message}). The start
              checks it again.
            </Alert>
          )}
          {runProblems.length > 0 && (
            <Alert severity="error">
              {runProblems.map((p, i) => (
                <div key={i}>{p.detail}</div>
              ))}
            </Alert>
          )}

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">Include</TableCell>
                <TableCell padding="checkbox">Creates the tour</TableCell>
                <TableCell>Product</TableCell>
                <TableCell>IDs it brings</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => {
                const isPrimary = r.id === primary;
                const minted = ctx.policy?.mintable ? mintForProduct(ctx.policy, r.product) : [];
                const server = r.isMember && !r.leftOut ? group?.members.find((m) => m.product_id === r.id) : undefined;
                const problems = isPrimary || (r.isMember && !r.leftOut) ? problemsFor(r.id) : [];
                return (
                  <Fragment key={r.id}>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={included.has(r.id)}
                          disabled={isPrimary}
                          inputProps={{ 'aria-label': `Include ${nameOf(r.id)}` }}
                          onChange={(e) =>
                            setIncluded((prev) => {
                              const next = new Set(prev);
                              if (e.target.checked) next.add(r.id);
                              else next.delete(r.id);
                              return next;
                            })
                          }
                        />
                      </TableCell>
                      <TableCell padding="checkbox">
                        <Radio
                          size="small"
                          checked={isPrimary}
                          disabled={!included.has(r.id)}
                          inputProps={{ 'aria-label': `${nameOf(r.id)} creates the tour` }}
                          onChange={() => {
                            setPrimary(r.id);
                            setIncluded((prev) => new Set(prev).add(r.id));
                            // What holds a member's IDs depends on the tour it
                            // joins: answers to "leave out" were given for the
                            // old one, so they are asked again.
                            setSeen(new Map());
                            setChoices(
                              (prev) => new Map([...prev].filter(([, c]) => c.kind !== 'leave')),
                            );
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{nameOf(r.id)}</Typography>
                        <Typography variant="caption" color="text.secondary" component="code">
                          {r.id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {!included.has(r.id) ? (
                          <Typography variant="caption" color="text.secondary">
                            not included: built on its own
                          </Typography>
                        ) : r.leftOut ? (
                          <Typography variant="caption" color="text.secondary">
                            left out
                          </Typography>
                        ) : (
                          <Stack spacing={0.25}>
                            {minted.map((m) => {
                              const ext = server?.ext_ids.find((e) => e.custom_id === m.value);
                              return (
                                <Typography key={m.value} variant="caption" component="div">
                                  <code>{m.value}</code>
                                  {isPrimary && ' (the tour’s own ID)'}
                                  {ext?.action === 'already' && ' (already an additional ID of this tour)'}
                                  {ext?.action === 'move' && ext.owner &&
                                    ` (moves from ${tourLabel({ id: ext.owner.tour_id, name: ext.owner.tour_name })})`}
                                </Typography>
                              );
                            })}
                          </Stack>
                        )}
                        {problems.map((p, i) => (
                          <Typography key={i} variant="caption" color="error" component="div">
                            {p.detail}
                          </Typography>
                        ))}
                        {isPrimary && splitFrom && (
                          <Chip
                            size="small"
                            color="info"
                            sx={{ mt: 0.5 }}
                            label={`splits off ${tourLabel(splitFrom)}`}
                            onDelete={() =>
                              setSplitOffs((prev) => {
                                const next = new Map(prev);
                                next.delete(r.id);
                                return next;
                              })
                            }
                            deleteIcon={<CancelIcon titleAccess="Undo split-off" />}
                          />
                        )}
                        {isPrimary && splitTour && (
                          <Box>
                            <Button
                              size="small"
                              sx={{ px: 0.5, minWidth: 0 }}
                              onClick={() => setSplitAsk({ id: r.id, tour: splitTour })}
                            >
                              Split off and build its own tour…
                            </Button>
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                    {r.isMember && r.needsChoice && (
                      <TableRow>
                        <TableCell colSpan={4} sx={{ pt: 0 }}>
                          <OwnershipBlock
                            name={nameOf(r.id)}
                            integration={scope.integration}
                            holders={r.holders}
                            choice={r.choice}
                            impact={serverMember(r.id)?.impact ?? []}
                            canMoveBookings={data?.can_move_bookings ?? false}
                            moveBookings={moveBookings.has(r.id)}
                            onChoose={(kind) =>
                              setChoice(
                                r.id,
                                kind === 'leave'
                                  ? { kind: 'leave' }
                                  : { kind: 'move', tours: consentTours(r.holders) },
                              )
                            }
                            onMoveBookings={(on) =>
                              setMoveBookingsDraft((prev) => {
                                const next = new Set(prev);
                                if (on) next.add(r.id);
                                else next.delete(r.id);
                                return next;
                              })
                            }
                          />
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>

          {radiosShown && (
            <BookingMoveAnswers
              title="Moving bookings: answer both for the run"
              answers={answers}
              onChange={setAnswers}
              oldTours={oldTours}
            />
          )}
        </Stack>
      </DialogContent>
      {splitAsk && (
        <SplitOffDialog
          onClose={() => setSplitAsk(null)}
          onConfirm={() => {
            setSplitOffs((prev) => new Map(prev).set(splitAsk.id, splitAsk.tour.id));
            setSplitAsk(null);
          }}
          scope={scope}
          policy={ctx.policy?.id ?? null}
          product={{ id: splitAsk.id, name: nameOf(splitAsk.id) }}
          fromTour={splitAsk.tour}
          productIds={[...ctx.selected]}
        />
      )}
      <DialogActions>
        {!canConfirm && unanswered.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto', pl: 2 }}>
            Choose leave out or move for {listLabels(unanswered.map((r) => nameOf(r.id)))}.
          </Typography>
        )}
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!canConfirm} onClick={() => onConfirm(draft)}>
          Combine
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Draft -> join state, and the preview of it
// ---------------------------------------------------------------------------

/** The run's state with this dialog's group: `members` join `primary`, the
 *  consented ones with their tours, the booking movers ticked, and the
 *  run-wide answers once both are given. */
export function buildDraftState(
  base: JoinState,
  d: {
    /** The dialog's products: their split-offs are `splitOffs`' (none if absent). */
    candidates: readonly string[];
    splitOffs: ReadonlyMap<string, number>;
    primary: string;
    members: string[];
    consents: ReadonlyMap<string, readonly number[]>;
    moveBookings: ReadonlySet<string>;
    answers: Partial<BookingMoveChoice>;
  },
): JoinState {
  let s = base;
  for (const id of d.candidates) s = setSplitOff(s, id, d.splitOffs.get(id) ?? null);
  if (d.members.length === 0) return s;
  s = combine(s, [d.primary, ...d.members], d.primary);
  for (const [id, tours] of d.consents) s = setMoveConsent(s, id, tours);
  for (const id of d.moveBookings) s = setMoveBookings(s, id, true);
  const { update_events, old_tour_tags } = d.answers;
  return setBookingMove(s, update_events && old_tour_tags ? { update_events, old_tour_tags } : null);
}

interface DraftInputs {
  productIds: string[];
  splitOffs: ReadonlyMap<string, number>;
  included: ReadonlySet<string>;
  primary: string;
  choices: ReadonlyMap<string, OwnershipChoice>;
  moveBookings: ReadonlySet<string>;
  answers: Partial<BookingMoveChoice>;
}

/** The preview of the draft, debounced. `isCurrent`: the answer in hand is
 *  for exactly the draft on screen.
 *
 *  The request is built from the raw choices (a member left out is not sent;
 *  a move sends the consented tours as chosen), so a consent that no longer
 *  covers the IDs' holders is still sent and the server reports it. */
function useJoinsPreview(
  scope: CombineProductsDialogProps['scope'],
  ctx: JoinContext,
  base: JoinState,
  d: DraftInputs,
) {
  const members = d.productIds.filter(
    (id) => id !== d.primary && d.included.has(id) && d.choices.get(id)?.kind !== 'leave',
  );
  const consents = new Map(
    members.flatMap((id) => {
      const c = d.choices.get(id);
      return c?.kind === 'move' ? [[id, c.tours] as const] : [];
    }),
  );
  const state = buildDraftState(base, {
    candidates: d.productIds,
    splitOffs: d.splitOffs,
    primary: d.primary,
    members,
    consents,
    moveBookings: new Set(members.filter((id) => d.moveBookings.has(id))),
    answers: d.answers,
  });
  const request: ProductJoinsPreviewInput | null =
    members.length > 0
      ? {
          ...scope,
          policy: ctx.policy?.id ?? null,
          product_ids: [...ctx.selected],
          ...toStartPayload(state),
        }
      : null;
  const requestKey = JSON.stringify(request);
  const debouncedKey = useDebouncedValue(requestKey, JOIN_PREVIEW_DEBOUNCE_MS);
  const query = useQuery<ProductJoinsPreview>({
    queryKey: ['sync', 'joins-preview', debouncedKey],
    queryFn: ({ signal }) =>
      syncApi.previewJoins(JSON.parse(debouncedKey) as ProductJoinsPreviewInput, signal),
    enabled: debouncedKey !== 'null',
    placeholderData: keepPreviousData,
    retry: false,
    staleTime: 0,
  });
  const settled = debouncedKey === requestKey;
  return {
    ...query,
    /** The request on screen is the one asked (the debounce has passed). */
    settled,
    isCurrent: query.isSuccess && !query.isPlaceholderData && settled,
  };
}
